import React, { ChangeEvent, forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Input } from '~/components';
import { ActionModalComponentProps } from '~/components/Modal/ActionModal/ActionModal';
import { GetConversationsDocument, useUpdateConversationMutation } from '~/types/api.graphql';

import { CONVERSATIONS_PER_PAGE } from '../AIInsightsSidebar.utils';
import { Container } from './UpdateConversationNameView.styles';

export interface UpdateConversationNameViewProps {
  conversationId: string;
  name: string;
}

const UpdateConversationNameView = forwardRef(
  (
    {
      conversationId,
      name,
      onLoadingChanged,
      setDisabledSubmitButton,
      onFinish,
    }: UpdateConversationNameViewProps & ActionModalComponentProps,
    ref,
  ) => {
    const { t } = useTranslation();
    const [nameLocal, setNameLocal] = useState(name);

    const [updateConversationMutation] = useUpdateConversationMutation({
      refetchQueries: [
        {
          query: GetConversationsDocument,
          variables: { first: CONVERSATIONS_PER_PAGE },
        },
      ],
    });

    useImperativeHandle(ref, () => ({
      submit: () => {
        void (async () => {
          try {
            onLoadingChanged?.(true);
            await updateConversationMutation({
              variables: { updateConversationInput: { conversationId, title: nameLocal.trim() } },
            });
            onLoadingChanged?.(false);
            onFinish?.();
          } catch (e: unknown) {
            onLoadingChanged?.(false);
          }
        })();
      },
    }));

    useEffect(() => {
      setDisabledSubmitButton?.(!nameLocal || nameLocal === name);
    }, [nameLocal, name, setDisabledSubmitButton]);

    return (
      <Container>
        <Input
          required
          fullWidth
          label={t('aiInsights.conversationNameLabel')}
          placeholder={t('aiInsights.conversationNamePlaceholder')}
          value={nameLocal}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setNameLocal(e.target.value)}
        />
      </Container>
    );
  },
);

export default UpdateConversationNameView;
